import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import {
  Box,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { css, styled as muiStyled } from '@mui/material/styles';
import React, { useState } from 'react';

import Arranger from '../../base/arranger';
import { useSnackbarContext } from '../../base/snackbar';
import MembersInvitePage from '../../members/invite-members';

import { useDashboardDataContext } from '~/client/components/project/dashboard/dashboard-context';
import { usePermissions } from '~/client/contexts/permissions-context';
import { useUserProfile } from '~/client/hooks/user-profile';
import * as GQL from '~/graphql/generated/client';
import Button from '~c/components/base/button';
import { Drawer } from '~c/components/base/drawer';
import Spacer, { spc } from '~c/components/base/spacer';
import Text from '~c/components/base/text';

interface Props {
  assessmentId: string;
  onClose: () => void;
  open: boolean;
  className?: string;
  defaultAssignee?: string;
}

interface AssignmentFormData {
  assignedTo: string;
  dueDate: string;
  comments: string;
}

type UIAssignment = GQL.AssignmentRef & {
  isArchived?: boolean;
  archivedAt?: number;
  archivedReason?: string;
};

const AssessmentAssignmentManagerImpl: React.FC<Props> = ({
  assessmentId,
  onClose,
  open,
  className,
  defaultAssignee,
}) => {
  const { project } = useDashboardDataContext();
  const { userProfile } = useUserProfile();
  const { hasCapability } = usePermissions();

  const Snackbar = useSnackbarContext();

  const [formData, setFormData] = useState<AssignmentFormData>({
    assignedTo: '',
    dueDate: '',
    comments: '',
  });

  const [openInvite, setOpenInvite] = useState(false);

  const [assignStakeholder, { loading: assignStakeholderLoading }] =
    GQL.useCreateAssessmentAssignmentMutation();

  const [removeStakeholder, { loading: removeStakeholderLoading }] =
    GQL.useRemoveAssessmentAssignmentMutation();

  const [
    transitionStuckAssessments,
    { loading: transitionStuckAssessmentsLoading },
  ] = GQL.useTransitionStuckAssessmentsMutation();

  const sharedEmails = (project?.sharedWith ?? [])
    .map((m) => m?.email)
    .filter(Boolean) as string[];
  const ownerEmail = project?.sharedWith?.find(
    (member) => member?.role === 'Owner'
  )?.email;

  const {
    data: activeUsersByEmailsData,
    loading: loadingActive,
    refetch: refetchActiveUsers,
  } = GQL.useActiveUsersByEmailsQuery({
    variables: {
      emails: sharedEmails,
    },
    skip: sharedEmails.length === 0,
    fetchPolicy: 'cache-and-network',
  });
  const activeUsers =
    activeUsersByEmailsData?.activeUsersByEmails.filter(
      (u) => u.email !== ownerEmail
    ) ?? [];

  const prefilled = React.useRef(false);
  React.useEffect(() => {
    const selectable = (
      activeUsersByEmailsData?.activeUsersByEmails ?? []
    ).some(
      (u) => u.email === defaultAssignee && u.email !== ownerEmail && u.isActive
    );
    if (!prefilled.current && defaultAssignee && selectable) {
      prefilled.current = true;
      setFormData((f) => ({ ...f, assignedTo: defaultAssignee }));
    }
  }, [activeUsersByEmailsData, defaultAssignee, ownerEmail]);

  // A project owner (or anyone with project:manage_members) can add stakeholders
  // directly from here so the responder list is never a dead end.
  const isProjectOwner = (project?.sharedWith ?? []).some(
    (m) => m?.email === userProfile?.email && m?.role === 'Owner'
  );
  const canManageMembers =
    isProjectOwner || hasCapability('project:manage_members');

  const handleCloseInvite = () => {
    setOpenInvite(false);
    if (sharedEmails.length > 0) {
      refetchActiveUsers();
    }
  };

  // Get current assignments for this assessment
  const currentAssignments =
    project?.assessments?.find((a) => a._id === assessmentId)?.assignments ??
    ([] as UIAssignment[]);

  const archivedAssignments =
    project?.assessments?.find((a) => a._id === assessmentId)
      ?.archivedAssignments ?? [];

  const allArchivedAssignments = archivedAssignments.flatMap((archivedGroup) =>
    archivedGroup?.assignments?.map((assignment) => ({
      ...assignment,
      isArchived: true,
      archivedAt: archivedGroup?.archivedAt,
      archivedReason: archivedGroup?.reason,
    }))
  ) as UIAssignment[];

  const allAssignments = [
    ...currentAssignments,
    ...allArchivedAssignments,
  ] as UIAssignment[];

  const handleAssign = async () => {
    if (!formData.assignedTo || !formData.dueDate) {
      return;
    }

    try {
      await assignStakeholder({
        variables: {
          input: {
            assessmentId,
            assignedTo: formData.assignedTo,
            dueDate: new Date(formData.dueDate).getTime(),
            type: GQL.AssignmentType.Submission,
            comments: formData.comments,
          },
        },
      }).then(() => {
        Snackbar.open(`${formData.assignedTo} assigned successfully`, {
          showOk: true,
        });
      });

      setFormData({ assignedTo: '', dueDate: '', comments: '' });
    } catch (err: any) {
      Snackbar.open(err.message || 'Failed to assign assessment', {
        showOk: true,
      });
    }
  };

  const handleRemoveAssignment = async (assignment: any) => {
    try {
      if (assignment.type !== GQL.AssignmentType.Submission) {
        Snackbar.open('Only submission assignments can be deleted', {
          showOk: true,
        });
        return;
      }
      // Create a unique identifier using assignedTo and dueDate
      const assignmentId = `${assignment.assignedTo}|${new Date(
        assignment?.dueDate ?? ''
      ).toISOString()}`;

      await removeStakeholder({
        variables: {
          input: {
            assessmentId,
            assignmentId,
          },
        },
      }).then(() => {
        Snackbar.open(`${assignment.assignedTo} removed successfully`, {
          showOk: true,
        });
      });
    } catch (err: any) {
      Snackbar.open(err.message || 'Failed to remove assignment', {
        showOk: true,
      });
    }
  };

  const handleTransitionStuckAssessments = async () => {
    if (!project?._id) {
      Snackbar.open('Project ID not found', { showOk: true });
      return;
    }

    try {
      const result = await transitionStuckAssessments({
        variables: {
          input: {
            projectId: project._id,
            assessmentId,
          },
        },
      });

      Snackbar.open(
        result.data?.transitionStuckAssessments?.message ??
          'Transition completed',
        {
          showOk: true,
        }
      );
    } catch (err: any) {
      Snackbar.open(err.message || 'Failed to transition stuck assessments', {
        showOk: true,
      });
    }
  };

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        anchor="right"
        width={650}
        title="Assign a Responder"
      >
        <div className={className}>
          <div className="drawer-content">
            <div className="comment-container">
              <div>
                <Arranger direction="row">
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Select Member</InputLabel>
                    <Select
                      value={formData.assignedTo}
                      onChange={(e) =>
                        setFormData({ ...formData, assignedTo: e.target.value })
                      }
                      label="Select Member"
                    >
                      {loadingActive && (
                        <MenuItem disabled value="">
                          <Box display="flex" alignItems="center" gap={1}>
                            <span>Loading users…</span>
                          </Box>
                        </MenuItem>
                      )}
                      {!loadingActive && activeUsers.length === 0 && (
                        <MenuItem disabled value="">
                          <span>No stakeholders Found</span>
                        </MenuItem>
                      )}
                      {activeUsers.map((u) => {
                        return (
                          <MenuItem
                            key={u.email}
                            value={u.email}
                            disabled={!u.isActive}
                          >
                            <Arranger direction="column" justify="center">
                              <div>
                                {' '}
                                <Text sz="body14">
                                  {`${u.firstName ?? ''} ${u.lastName ?? ''}`.trim()}
                                </Text>
                              </div>
                              <div>
                                <Text sz="body12">{u.email}</Text>
                              </div>
                            </Arranger>
                          </MenuItem>
                        );
                      })}
                    </Select>
                    <Arranger
                      direction="row"
                      justify="space-between"
                      alignItems="center"
                    >
                      <div className="members-link-container">
                        {canManageMembers && (
                          <Text sz="body12">
                            Need to add a stakeholder ?{' '}
                            <span
                              className="members-link"
                              onClick={() => setOpenInvite(true)}
                            >
                              Click here
                            </span>{' '}
                          </Text>
                        )}
                      </div>
                    </Arranger>
                  </FormControl>

                  <Spacer pl={2} />
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Due Date"
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) =>
                      setFormData({ ...formData, dueDate: e.target.value })
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Arranger>

                <TextField
                  fullWidth
                  margin="normal"
                  label="Comments (Optional)"
                  multiline
                  rows={3}
                  value={formData.comments}
                  onChange={(e) =>
                    setFormData({ ...formData, comments: e.target.value })
                  }
                />
              </div>
              <Box mt={3} display="flex" gap={2} justifyContent="flex-end">
                <Button
                  onClick={handleAssign}
                  loading={assignStakeholderLoading}
                  disabled={!formData.assignedTo || !formData.dueDate}
                  color="primary"
                >
                  Assign
                </Button>
              </Box>
              <div className="current-assignments">
                <List>
                  {allAssignments.map((assignment, index) => (
                    <ListItem key={index} divider>
                      <ListItemText
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontWeight: 'bold',
                            opacity: assignment?.isArchived ? 0.7 : 1,
                          },
                          '& .MuiListItemText-secondary': {
                            opacity: assignment?.isArchived ? 0.7 : 1,
                          },
                        }}
                        primary={assignment?.assignedTo ?? ''}
                        secondary={`Type: ${assignment?.type ?? ''} | Due: ${new Date(
                          assignment?.dueDate ?? ''
                        ).toLocaleDateString()} | Status: ${assignment?.status ?? ''}${
                          assignment?.isArchived
                            ? ` | Archived: ${new Date(assignment?.archivedAt ?? '').toLocaleDateString()}`
                            : ''
                        }`}
                      />
                    </ListItem>
                  ))}
                </List>
              </div>
            </div>{' '}
          </div>{' '}
        </div>
      </Drawer>
      {openInvite && (
        <MembersInvitePage open={openInvite} onClose={handleCloseInvite} />
      )}
    </>
  );
};

export const AssessmentAssignmentManager = muiStyled(
  AssessmentAssignmentManagerImpl
)(
  ({ theme }) => css`
    .members-link-container {
      margin-top: ${theme.spacing(spc.xxxs)};
      margin-bottom: ${theme.spacing(spc.m)};
    }

    .members-link {
      color: ${theme.palette.error.main};
      cursor: pointer;
      text-decoration: underline;
      font-weight: 500;

      &:hover {
        color: ${theme.palette.error.main};
        text-decoration: none;
      }
    }
    .drawer-content {
      padding: ${theme.spacing(4)} ${theme.spacing(4)};

      .comment-container {
        border: 1px solid ${theme.palette.divider};
        border-radius: 4px;
        padding: ${theme.spacing(4)} ${theme.spacing(4)};
        background-color: ${theme.palette.background.paper};
        min-height: 180px;
        max-height: 780px;
        display: flex;
        flex-direction: column;
        gap: 0;

        .container-title {
          margin-bottom: ${theme.spacing(2)};
          font-weight: 500;
        }

        .message-section {
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: ${theme.spacing(1)};
          margin-bottom: ${theme.spacing(2)};
          border: 1px solid ${theme.palette.divider};
          border-radius: 4px;
          padding: ${theme.spacing(2)};
          min-height: 150px;
        }

        .top-section {
          border: 1px solid ${theme.palette.divider};
          border-radius: 4px 4px 0 0;
          padding: 0;
          background-color: ${theme.palette.background.paper};

          .MuiTextField-root {
            padding: 0;

            .MuiOutlinedInput-root {
              border-radius: 4px 4px 0 0;
            }
          }
        }

        .bottom-section {
          /* border: 2px solid #999999;
          border-radius: 0 0 4px 4px; */
          border-top: none;
          padding: ${theme.spacing(1)};
          background-color: ${theme.palette.background.paper};
          height: 30px;
          margin-top: -2px;

          .bottom-content {
            display: flex;
            justify-content: flex-end;
            align-items: center;
            height: 100%;
          }
        }
      }
    }
    .current-assignments {
      margin-top: ${theme.spacing(4)};
      /* border: 1px solid ${theme.palette.divider};
      border-radius: 4px; */
      padding: ${theme.spacing(2)};
      background-color: ${theme.palette.background.paper};
      min-height: 150px;
      max-height: 300px;
      overflow-y: auto;
    }
  `
);

export default AssessmentAssignmentManager;
